import { trackDownload } from '../../utils/downloadIndicator';
import React, { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  Divider,
  Grid,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import PublicPageShell from '../../components/PublicPageShell';
import { addStruxorPdfFooter, reservePdfFooterSpace } from '../../utils/pdfBranding';

const PRIMARY = '#0087c1';

const UNITS = ['each', 'm²', 'm', 'hr', 'day', 'lot'];

const DEFAULT_TERMS =
  'Payment terms: 30 days from date of invoice.\n' +
  'This quote is valid for 30 days from date of quote.\n' +
  'Any variations to the scope of work will be quoted separately.\n' +
  'All work carried out in accordance with current building regulations.\n'

const genQuoteNumber = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `Q-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${Math.floor(Math.random() * 900 + 100)}`;
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const plus30 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

let nextLineId = 2;

const emptyLine = (id) => ({ id, description: '', qty: '1', unit: 'each', unitPrice: '', total: 0 });

const fmt = (n) =>
  Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function QuotingTemplate() {
  const theme = useTheme();

  // Company details
  const [company, setCompany] = useState({ name: '', address: '', phone: '', email: '' });

  // Client details
  const [client, setClient] = useState({ name: '', address: '', email: '', phone: '' });

  // Quote meta
  const [quoteMeta, setQuoteMeta] = useState({
    number: genQuoteNumber(),
    date: todayISO(),
    validUntil: plus30(),
  });

  // Scope
  const [scope, setScope] = useState('');

  // Line items
  const [lines, setLines] = useState([emptyLine(1)]);

  // VAT
  const [vatRate, setVatRate] = useState('20');

  // Terms & notes
  const [terms, setTerms] = useState(DEFAULT_TERMS);
  const [notes, setNotes] = useState('');

  // ───── derived totals ─────
  const computedLines = lines.map((l) => {
    const qty = parseFloat(l.qty) || 0;
    const price = parseFloat(l.unitPrice) || 0;
    return { ...l, total: qty * price };
  });
  const subtotal = computedLines.reduce((s, l) => s + l.total, 0);
  const vatPct = parseFloat(vatRate) || 0;
  const vatAmount = subtotal * (vatPct / 100);
  const grandTotal = subtotal + vatAmount;

  // ───── line-item helpers ─────
  const addLine = () => setLines((prev) => [...prev, emptyLine(nextLineId++)]);
  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.id !== id));

  const updateLine = (id, field, value) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)));
  };

  // ───── PDF generation ─────
  const downloadPDF = useCallback(async () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    // Header — company name
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text(company.name || 'Your Company Name', 14, y);
    y += 8;

    // Company address block
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (company.address) { doc.text(company.address, 14, y); y += 4; }
    if (company.phone) { doc.text(`Tel: ${company.phone}`, 14, y); y += 4; }
    if (company.email) { doc.text(`Email: ${company.email}`, 14, y); y += 4; }

    // Quote title
    y += 4;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('QUOTATION', pageWidth - 14, 20, { align: 'right' });

    // Quote meta — right side
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metaX = pageWidth - 14;
    doc.text(`Quote #: ${quoteMeta.number}`, metaX, 28, { align: 'right' });
    doc.text(`Date: ${quoteMeta.date}`, metaX, 33, { align: 'right' });
    doc.text(`Valid until: ${quoteMeta.validUntil}`, metaX, 38, { align: 'right' });

    // Divider
    y += 2;
    doc.setDrawColor(200);
    doc.line(14, y, pageWidth - 14, y);
    y += 6;

    // Client details
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 14, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    if (client.name) { doc.text(client.name, 14, y); y += 4; }
    if (client.address) { doc.text(client.address, 14, y); y += 4; }
    if (client.email) { doc.text(client.email, 14, y); y += 4; }
    if (client.phone) { doc.text(`Tel: ${client.phone}`, 14, y); y += 4; }

    // Scope of work
    if (scope.trim()) {
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Scope of Work', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const scopeLines = doc.splitTextToSize(scope, pageWidth - 28);
      doc.text(scopeLines, 14, y);
      y += scopeLines.length * 4 + 2;
    }

    // Line items table
    y += 4;
    const tableBody = computedLines.map((l) => [
      l.description,
      l.qty,
      l.unit,
      `£${fmt(parseFloat(l.unitPrice) || 0)}`,
      `£${fmt(l.total)}`,
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Description', 'Qty', 'Unit', 'Unit Price', 'Total']],
      body: tableBody,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [0, 135, 193], textColor: 255, fontStyle: 'bold' },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'center', cellWidth: 20 },
        2: { halign: 'center', cellWidth: 20 },
        3: { halign: 'right', cellWidth: 28 },
        4: { halign: 'right', cellWidth: 28 },
      },
      margin: { left: 14, right: 14, bottom: 24 },
    });

    y = doc.lastAutoTable.finalY + 6;

    // Totals
    y = reservePdfFooterSpace(doc, y, 18);
    const totalsX = pageWidth - 14;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Subtotal:`, totalsX - 40, y);
    doc.text(`£${fmt(subtotal)}`, totalsX, y, { align: 'right' });
    y += 5;
    doc.text(`VAT (${vatPct}%):`, totalsX - 40, y);
    doc.text(`£${fmt(vatAmount)}`, totalsX, y, { align: 'right' });
    y += 5;
    doc.setFont('helvetica', 'bold');
    doc.text(`Grand Total:`, totalsX - 40, y);
    doc.text(`£${fmt(grandTotal)}`, totalsX, y, { align: 'right' });

    // Terms
    if (terms.trim()) {
      const termLines = doc.splitTextToSize(terms, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 10, termLines.length * 3.5 + 8);
      y += 10;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Terms & Conditions', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(termLines, 14, y);
      y += termLines.length * 3.5 + 2;
    }

    // Notes
    if (notes.trim()) {
      const noteLines = doc.splitTextToSize(notes, pageWidth - 28);
      y = reservePdfFooterSpace(doc, y + 4, noteLines.length * 3.5 + 8);
      y += 4;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text('Notes', 14, y);
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(noteLines, 14, y);
    }

    await addStruxorPdfFooter(doc);
    doc.save(`${quoteMeta.number}.pdf`);
  }, [company, client, quoteMeta, scope, computedLines, subtotal, vatPct, vatAmount, grandTotal, terms, notes]);

  // ───── section header helper ─────
  const SectionTitle = ({ children }) => (
    <Typography variant="h6" fontWeight={700} sx={{ color: PRIMARY, mb: 2 }}>
      {children}
    </Typography>
  );

  // ───── render ─────
  return (
    <>
      <Helmet>
        <title>Free Construction Quote Template — Create &amp; Download PDF | Struxor</title>
        <meta
          name="description"
          content="Free construction quote template. Fill in your company details, line items and terms, then download a professional PDF quote. No sign-up required."
        />
        <link rel="canonical" href="https://www.struxorapp.com/tools/quote-template" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Construction Quote Template',
          description: 'Free online construction quoting template. Create professional quotes with line items, VAT calculations, and download as PDF.',
          url: 'https://www.struxorapp.com/tools/quote-template',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'GBP' },
          author: { '@type': 'Organization', name: 'Struxor', url: 'https://www.struxorapp.com' },
        })}</script>
      </Helmet>

      <PublicPageShell>

      {/* ── Hero ── */}
      <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #005a8c 100%)`, color: '#fff', py: 6, px: 2, textAlign: 'center' }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" mb={2}>
          <DescriptionIcon sx={{ fontSize: 36 }} />
          <Typography variant="h4" component="h1" fontWeight={700}>
            Construction Quote Template
          </Typography>
        </Stack>
        <Typography variant="h6" sx={{ opacity: 0.9, maxWidth: 640, mx: 'auto', fontWeight: 400 }}>
          Create professional construction quotes and download them as PDF. Free, no sign-up required.
        </Typography>
      </Box>

      <Container maxWidth="lg" sx={{ py: 5 }}>

        {/* ══════════ Company Details ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Your Company Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Company Name" size="small" value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" size="small" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Address" size="small" value={company.address} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" size="small" value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Client Details ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Client Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client Name" size="small" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client Email" size="small" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client Address" size="small" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Client Phone" size="small" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Quote Meta ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Quote Details</SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Quote Number" size="small" value={quoteMeta.number} onChange={(e) => setQuoteMeta({ ...quoteMeta, number: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Date" size="small" type="date" InputLabelProps={{ shrink: true }} value={quoteMeta.date} onChange={(e) => setQuoteMeta({ ...quoteMeta, date: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Valid Until" size="small" type="date" InputLabelProps={{ shrink: true }} value={quoteMeta.validUntil} onChange={(e) => setQuoteMeta({ ...quoteMeta, validUntil: e.target.value })} />
            </Grid>
          </Grid>
        </Paper>

        {/* ══════════ Scope of Work ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Project Description / Scope of Work</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Describe the project scope, deliverables, site address, etc."
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Line Items ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, mb: 3, overflow: 'hidden' }}>
          <Box sx={{ background: alpha(PRIMARY, 0.06), px: 3, py: 2 }}>
            <SectionTitle>Line Items</SectionTitle>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: theme.palette.background.default }}>
                  <TableCell sx={{ fontWeight: 700, minWidth: 240 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 80 }} align="center">Qty</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 90 }} align="center">Unit</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }} align="right">Unit Price (£)</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 110 }} align="right">Total (£)</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {computedLines.map((line) => (
                  <TableRow key={line.id}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g. Supply and install kitchen units"
                        value={line.description}
                        onChange={(e) => updateLine(line.id, 'description', e.target.value)}
                        sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        value={line.qty}
                        onChange={(e) => updateLine(line.id, 'qty', e.target.value)}
                        sx={{ width: 70, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'center' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        select
                        size="small"
                        value={line.unit}
                        onChange={(e) => updateLine(line.id, 'unit', e.target.value)}
                        sx={{ width: 80, '& .MuiInputBase-input': { fontSize: 13 } }}
                      >
                        {UNITS.map((u) => (
                          <MenuItem key={u} value={u}>{u}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: 'any' }}
                        placeholder="0.00"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(line.id, 'unitPrice', e.target.value)}
                        sx={{ width: 100, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        £{fmt(line.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {lines.length > 1 && (
                        <IconButton size="small" onClick={() => removeLine(line.id)} sx={{ color: theme.palette.error.main }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Button startIcon={<AddIcon />} onClick={addLine} variant="outlined" sx={{ borderColor: PRIMARY, color: PRIMARY, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              Add Line Item
            </Button>

            {/* Totals */}
            <Box sx={{ minWidth: 260 }}>
              <Stack direction="row" justifyContent="space-between" mb={0.5}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>£{fmt(subtotal)}</Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2">VAT</Typography>
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 'any' }}
                    value={vatRate}
                    onChange={(e) => setVatRate(e.target.value)}
                    sx={{ width: 64, '& .MuiInputBase-input': { fontSize: 13, textAlign: 'center', py: '4px' } }}
                  />
                  <Typography variant="body2">%:</Typography>
                </Stack>
                <Typography variant="body2" fontWeight={600}>£{fmt(vatAmount)}</Typography>
              </Stack>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: PRIMARY }}>Grand Total:</Typography>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: PRIMARY }}>£{fmt(grandTotal)}</Typography>
              </Stack>
            </Box>
          </Box>
        </Paper>

        {/* ══════════ Terms & Conditions ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 3 }}>
          <SectionTitle>Terms &amp; Conditions</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={terms}
            onChange={(e) => setTerms(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Notes ══════════ */}
        <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3, mb: 4 }}>
          <SectionTitle>Notes / Special Conditions</SectionTitle>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Any additional notes, exclusions, or special conditions…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
          />
        </Paper>

        {/* ══════════ Download Button ══════════ */}
        <Stack direction="row" spacing={2} mb={6} justifyContent="center">
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => trackDownload(downloadPDF, { defer: true })()}
            variant="contained"
            size="large"
            sx={{ background: PRIMARY, '&:hover': { background: '#006fa0' }, textTransform: 'none', fontWeight: 700, borderRadius: 2, px: 5 }}
          >
            Download PDF
          </Button>
        </Stack>

        {/* ══════════ Tips ══════════ */}
        <Box mb={5}>
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <LightbulbIcon sx={{ color: PRIMARY }} />
            <Typography variant="h5" fontWeight={700}>Tips for Writing Effective Construction Quotes</Typography>
          </Stack>
          <Grid container spacing={3}>
            {[
              { title: 'Be Specific with Scope', body: 'Clearly define what is and is not included. Ambiguity leads to disputes. Reference drawings, specifications, and site conditions.' },
              { title: 'Include Payment Terms', body: 'State when payment is due, accepted methods, and any deposit requirements. Clear payment terms reduce late payments and improve cash flow.' },
              { title: 'Set a Validity Period', body: 'Material prices and labour rates fluctuate. Always state how long the quote is valid — 30 days is standard for most construction work.' },
              { title: 'Break Down Costs', body: 'Itemise labour, materials, plant hire, and prelims separately. Transparency builds trust and makes it easier for clients to compare quotes.' },
              { title: 'List Exclusions Clearly', body: 'State what is excluded — structural alterations, party wall agreements, skip hire, etc. This protects you from scope creep and extra costs.' },
              { title: 'Add Provisional Sums', body: 'For elements that cannot be accurately priced upfront (e.g. groundwork), include provisional sums and explain they are estimates subject to adjustment.' },
            ].map(({ title, body }) => (
              <Grid item xs={12} sm={6} md={4} key={title}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5, height: '100%' }}>
                  <Typography fontWeight={700} mb={1}>{title}</Typography>
                  <Typography variant="body2" color="text.secondary">{body}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ══════════ How Struxor Extends This ══════════ */}
        <Paper elevation={0} sx={{ border: `2px solid ${PRIMARY}`, borderRadius: 3, p: { xs: 3, md: 4 }, mb: 5 }}>
          <Typography variant="h5" fontWeight={700} mb={1}>
            How Struxor Extends Your Quoting
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            This free template gets the job done — but Struxor's built-in quote assistant takes construction quoting to the next level.
          </Typography>
          <Grid container spacing={2}>
            {[
              'AI-powered quote generation from project briefs and photos',
              'Automatic project and contact linking',
              'Professional, branded PDF templates with your Logo and colours',
              'The system pre-populates any known fields',
              'Convert accepted quotes into projects in one click',
              'Auto-review to ensure your quotes are priced in line with expectations and worded clearly'
            ].map((item) => (
              <Grid item xs={12} sm={6} key={item}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleIcon sx={{ color: PRIMARY, fontSize: 20 }} />
                  <Typography variant="body2">{item}</Typography>
                </Stack>
              </Grid>
            ))}
          </Grid>
          <Box mt={3}>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              sx={{ background: PRIMARY, '&:hover': { background: '#006fa0' }, textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Try Struxor Free for 14 Days
            </Button>
          </Box>
        </Paper>

        {/* ══════════ FAQ ══════════ */}
        <Box mb={5}>
          <Typography variant="h5" fontWeight={700} mb={3}>Frequently Asked Questions</Typography>
          <Grid container spacing={3}>
            {[
              {
                q: 'What should a construction quote include?',
                a: 'A professional construction quote should include your company details, client information, a clear scope of work, itemised costs with quantities and rates, VAT, payment terms, validity period, and any exclusions or assumptions.',
              },
              {
                q: 'How do I price a construction job accurately?',
                a: 'Start by measuring the work precisely, then price labour (hours × rate), materials (with waste allowance), plant/equipment hire, and overheads. Add your margin on top. Use historical data from past projects to benchmark and refine your pricing.',
              },
              {
                q: 'What is the difference between a quote, an estimate, and a tender?',
                a: 'A quote is a fixed-price offer for defined work. An estimate is an approximate cost indication that may change. A tender is a formal response to a client\'s invitation to bid, usually for larger projects with detailed specifications.',
              },
              {
                q: 'How long should a construction quote be valid?',
                a: '30 days is standard for most domestic and small commercial work. For larger projects or volatile markets, 14 days may be more appropriate. Always state the validity period clearly to protect against material price rises.',
              },
            ].map(({ q, a }) => (
              <Grid item xs={12} md={6} key={q}>
                <Paper elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, p: 2.5 }}>
                  <Typography fontWeight={700} mb={1}>{q}</Typography>
                  <Typography variant="body2" color="text.secondary">{a}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>


        {/* ══════════ Footer Links ══════════ */}
        <Divider sx={{ mb: 3 }} />
        <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" mb={4}>
          <Button component={RouterLink} to="/tools/purchase-order-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Purchase Order Template
          </Button>
          <Button component={RouterLink} to="/tools/invoice-template" variant="text" sx={{ textTransform: 'none', color: PRIMARY }}>
            → Invoice Template
          </Button>
          <Button component={RouterLink} to="/" variant="text" sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Back to Struxor Home
          </Button>
        </Stack>
      </Container>
      </PublicPageShell>
    </>
  );
}
